import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Filter,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { toast } from '../hooks/use-toast';

const AbsenceCalendar = ({ 
  employees = [], 
  onDateSelect = null,
  selectedDates = null,
  showConflicts = true 
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    employee_id: '',
    leave_type: ''
  });
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, filters]);

  useEffect(() => {
    if (selectedDates && showConflicts) {
      checkForConflicts();
    }
  }, [selectedDates, calendarEvents]);

  const loadCalendarData = async () => {
    setLoading(true);
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const startDate = startOfMonth.toISOString().split('T')[0];
      const endDate = endOfMonth.toISOString().split('T')[0];
      
      // Build filters object for the API call
      const apiFilters = {};
      if (filters.employee_id) {
        apiFilters.employee_ids = filters.employee_id;
      }
      if (filters.department) {
        apiFilters.departments = filters.department;
      }
      if (filters.leave_type) {
        apiFilters.leave_types = filters.leave_type;
      }
      
      // Use apiService.getAbsenceCalendar method
      // Requirement: 8.1 - Frontend calls /api/absences/calendar endpoint
      // Requirement: 8.2 - System shows all absences within the date range
      const events = await apiService.getAbsenceCalendar(startDate, endDate, apiFilters);
      setCalendarEvents(events);
      
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load calendar data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const checkForConflicts = async () => {
    if (!selectedDates?.start || !selectedDates?.end || !selectedDates?.employee_id) return;
    
    try {
      // Use apiService.checkAbsenceConflicts method
      // Requirement: 8.3 - System calls /api/absences/conflicts/{employee_id}
      const result = await apiService.checkAbsenceConflicts(
        selectedDates.employee_id,
        selectedDates.start,
        selectedDates.end
      );
      
      setConflicts(result.conflicts || []);
    } catch (error) {
      console.error('Failed to check conflicts:', error);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const getEventsForDate = (day) => {
    if (!day) return [];
    
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      .toISOString().split('T')[0];
    
    return calendarEvents.filter(event => {
      const eventStart = event.start_datetime.split('T')[0];
      const eventEnd = event.end_datetime.split('T')[0];
      return dateStr >= eventStart && dateStr <= eventEnd;
    });
  };

  const isDateInRange = (day) => {
    if (!selectedDates?.start || !selectedDates?.end || !day) return false;
    
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      .toISOString().split('T')[0];
    
    return dateStr >= selectedDates.start && dateStr <= selectedDates.end;
  };

  const hasConflict = (day) => {
    if (!showConflicts || !day) return false;
    
    const events = getEventsForDate(day);
    const dayStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      .toISOString().split('T')[0];
    
    // Check for multiple events on the same day
    if (events.length > 1) return true;
    
    // Check for conflicts that affect this specific day
    return conflicts.some(conflict => {
      if (conflict.conflict_type === 'employee_overlap') {
        // Check if conflict affects this day
        if (selectedDates && selectedDates.start && selectedDates.end) {
          return dayStr >= selectedDates.start && dayStr <= selectedDates.end;
        }
      }
      return false;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'denied': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLeaveTypeColor = (leaveType) => {
    switch (leaveType) {
      case 'PTO': return 'bg-blue-500';
      case 'SICK': return 'bg-red-500';
      case 'PERSONAL': return 'bg-purple-500';
      case 'MEDICAL': return 'bg-orange-500';
      case 'EMERGENCY': return 'bg-red-600';
      default: return 'bg-gray-500';
    }
  };

  const handleDateClick = (day) => {
    if (!day || !onDateSelect) return;
    
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
      .toISOString().split('T')[0];
    
    onDateSelect(dateStr);
  };

  const uniqueDepartments = [...new Set(employees.map(emp => emp.department).filter(Boolean))];

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Absence Calendar
            </CardTitle>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(-1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="font-medium min-w-[150px] text-center">
                {currentDate.toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateMonth(1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select
              value={filters.department}
              onValueChange={(value) => setFilters(prev => ({ ...prev, department: value }))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Departments</SelectItem>
                {uniqueDepartments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.employee_id}
              onValueChange={(value) => setFilters(prev => ({ ...prev, employee_id: value }))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Employees</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={filters.leave_type}
              onValueChange={(value) => setFilters(prev => ({ ...prev, leave_type: value }))}
            >
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Leave Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Types</SelectItem>
                <SelectItem value="PTO">PTO</SelectItem>
                <SelectItem value="SICK">Sick</SelectItem>
                <SelectItem value="PERSONAL">Personal</SelectItem>
                <SelectItem value="MEDICAL">Medical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day Headers */}
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 border-b">
                {day}
              </div>
            ))}
            
            {/* Calendar Days */}
            {getDaysInMonth().map((day, index) => {
              const events = getEventsForDate(day);
              const inRange = isDateInRange(day);
              const hasConflictOnDay = hasConflict(day);
              
              return (
                <div
                  key={index}
                  className={`
                    min-h-[80px] p-1 border border-gray-200 cursor-pointer transition-colors
                    ${day ? 'hover:bg-gray-50' : ''}
                    ${inRange ? 'bg-blue-50 border-blue-300' : ''}
                    ${hasConflictOnDay ? 'bg-red-50 border-red-300' : ''}
                  `}
                  onClick={() => handleDateClick(day)}
                >
                  {day && (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{day}</span>
                        {hasConflictOnDay && (
                          <AlertTriangle className="w-3 h-3 text-red-500" />
                        )}
                      </div>
                      
                      <div className="space-y-1">
                        {events.slice(0, 2).map((event, eventIndex) => (
                          <div
                            key={eventIndex}
                            className={`
                              text-xs p-1 rounded border
                              ${getStatusColor(event.status)}
                            `}
                            title={`${event.employee_name} - ${event.leave_type} (${event.status})`}
                          >
                            <div className="flex items-center space-x-1">
                              <div 
                                className={`w-2 h-2 rounded-full ${getLeaveTypeColor(event.leave_type)}`}
                              />
                              <span className="truncate">
                                {event.employee_name.split(' ')[0]}
                              </span>
                              {event.is_partial_day && (
                                <Clock className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {events.length > 2 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{events.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="mt-4 pt-4 border-t">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-medium">Status:</span>
                <Badge variant="secondary" className="bg-green-100 text-green-800">Approved</Badge>
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-800">Denied</Badge>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="font-medium">Types:</span>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>PTO</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span>Sick</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                  <span>Personal</span>
                </div>
              </div>
              
              {showConflicts && (
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span>Conflicts detected</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Conflicts Panel */}
      {conflicts.length > 0 && showConflicts && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-orange-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Scheduling Conflicts Detected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conflicts.map((conflict, index) => (
                <div key={index} className="p-3 border border-orange-200 rounded-lg bg-orange-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-orange-800">
                        {conflict.conflict_type.replace('_', ' ').toUpperCase()}
                      </h4>
                      <p className="text-sm text-orange-700 mt-1">
                        {conflict.message}
                      </p>
                      
                      {conflict.suggested_alternatives?.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-orange-800">Suggestions:</span>
                          <ul className="text-xs text-orange-700 mt-1 list-disc list-inside">
                            {conflict.suggested_alternatives.map((suggestion, i) => (
                              <li key={i}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    <Badge variant="outline" className="border-orange-300 text-orange-700">
                      {conflict.impact_level}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AbsenceCalendar;